import { useMemo, useCallback, useEffect } from "react";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/library/accordion";
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from "@/components/ui/library/multi-select";
import {
  useFilterValues,
  useNicheFilterUpdate,
} from "@/features/leads/components/leadFilter/context/FilterContext";
import { useUniversityOptions } from "@/features/universityAndCourse/api/useUniversities";
import { useCourseOptions } from "@/features/universityAndCourse/api/useCourses";
import { child_spesific_key } from "@/config/endpoints";
import CustomFieldFilters, {
  useSelectorCustomFieldsForModel,
} from "../Preferences/CustomFieldFilters";
import HighlightText from "../HighlightText";
import { useIsSectionActive } from "../../context/AppliedFiltersContext";

export default function StudyPreferences() {
  const sectionActive = useIsSectionActive("preference");
  const { nicheFilters, customFieldFilters } = useFilterValues();
  const updateNiche = useNicheFilterUpdate();

  const universityRaw = (nicheFilters.universityId as string) || "";
  const courseRaw = (nicheFilters.courseId as string) || "";

  const selectedUniversities = universityRaw
    ? universityRaw.split(",").filter(Boolean)
    : [];
  const selectedCourses = courseRaw ? courseRaw.split(",").filter(Boolean) : [];

  // ── Interconnected cascade ─────────────────────────────────────────
  // The connection between a CF and the niche dropdowns is discovered from
  // the FIELD DEFINITION (its `modules` array) — never hardcoded. A field
  // like Country whose modules include "university" constrains the
  // University dropdown to universities carrying any selected value; the
  // Course dropdown follows both the selected universities (Course has a
  // university FK) and any course/university-scoped CF selections (the
  // server matches the course OR its university).
  const { selectorFields } = useSelectorCustomFieldsForModel(
    child_spesific_key,
    "child"
  );

  const selectedCfIdsFor = useCallback(
    (fieldId: number | string): string[] => {
      const raw = (customFieldFilters as Record<string, string>)[
        String(fieldId)
      ];
      if (!raw) return [];
      // "none" is the leads-side "has no value" sentinel — it can't
      // constrain which universities/courses exist, so skip it here.
      return raw.split(",").filter((v) => v && v !== "none");
    },
    [customFieldFilters]
  );

  const universityCascadeParams = useMemo(() => {
    const params: Record<string, string> = {};
    for (const field of selectorFields as any[]) {
      const mods: string[] = Array.isArray(field.modules) ? field.modules : [];
      if (!mods.includes("university")) continue;
      const ids = selectedCfIdsFor(field.id);
      if (ids.length) params[`cf_${field.id}`] = ids.join(",");
    }
    return params;
  }, [selectorFields, selectedCfIdsFor]);

  const courseCascadeParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (universityRaw) params.universityId = universityRaw;
    for (const field of selectorFields as any[]) {
      const mods: string[] = Array.isArray(field.modules) ? field.modules : [];
      if (!mods.includes("course") && !mods.includes("university")) continue;
      const ids = selectedCfIdsFor(field.id);
      if (ids.length) params[`cf_${field.id}`] = ids.join(",");
    }
    return params;
  }, [universityRaw, selectorFields, selectedCfIdsFor]);

  const { unversitySelects, isLoading: universitiesLoading } =
    useUniversityOptions(universityCascadeParams);
  const courseOptionsResult = useCourseOptions({
    params: courseCascadeParams,
  });
  const courseSelects = useMemo(
    () =>
      (courseOptionsResult.courses ?? []).map((c: any) => ({
        value: String(c.id),
        name: c.name,
      })),
    [courseOptionsResult.courses]
  );
  const coursesLoading = courseOptionsResult.isLoading;

  const universityOptions = useMemo(
    () =>
      (unversitySelects ?? []).map((opt: { value: string; name: string }) => ({
        value: opt.value,
        label: opt.name,
      })),
    [unversitySelects]
  );

  const courseOptions = useMemo(
    () =>
      (courseSelects ?? []).map((opt: { value: string; name: string }) => ({
        value: opt.value,
        label: opt.name,
      })),
    [courseSelects]
  );

  const handleUniversityChange = useCallback(
    (values: string[]) => {
      updateNiche("universityId", values.join(","));
    },
    [updateNiche]
  );

  const handleCourseChange = useCallback(
    (values: string[]) => {
      updateNiche("courseId", values.join(","));
    },
    [updateNiche]
  );

  // Prune selections that fell outside the cascaded option set (e.g. a UK
  // university was selected, then Country switched to Germany). Without
  // this the lead query would silently AND contradictory filters and
  // return nothing. Guarded on the loading flags so an in-flight refetch
  // (options momentarily empty) never wipes a valid selection.
  useEffect(() => {
    if (universitiesLoading || selectedUniversities.length === 0) return;
    const valid = new Set(
      (unversitySelects ?? []).map((o: { value: string }) => o.value)
    );
    const kept = selectedUniversities.filter((v) => valid.has(v));
    if (kept.length !== selectedUniversities.length) {
      updateNiche("universityId", kept.join(","));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unversitySelects, universitiesLoading, universityRaw]);

  useEffect(() => {
    if (coursesLoading || selectedCourses.length === 0) return;
    const valid = new Set(
      (courseSelects ?? []).map((o: { value: string }) => o.value)
    );
    const kept = selectedCourses.filter((v) => valid.has(v));
    if (kept.length !== selectedCourses.length) {
      updateNiche("courseId", kept.join(","));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseSelects, coursesLoading, courseRaw]);

  return (
    <div className="flex flex-col overflow-hidden">
      <Accordion
        type="single"
        collapsible
        className="w-full"
        defaultValue={sectionActive ? "study-preferences" : undefined}
      >
        <AccordionItem value="study-preferences" className="border-none">
          <AccordionTrigger className="text-b3-mid font-semibold bg-transparent bg-[var(--color-green-1)] px-4 py-3 rounded-none hover:no-underline">
            <HighlightText text="Study Preferences" />
          </AccordionTrigger>
          <AccordionContent className="px-5 py-2 bg-white">
            <div className="space-y-[14px] px-2 pb-4">
              {/* CF fields first (Country, Intake Year, …) — they drive the
                  University/Course cascade below, so they read top-down. */}
              <CustomFieldFilters
                queryKey={child_spesific_key}
                modelName="child"
                skipKeyFields
              />

              <div className="space-y-2">
                <Label className="text-[14px] leading-[1.35] tracking-[-0.56px] text-[#051912] font-normal mb-0 pb-1.5">
                  <HighlightText text="University" />
                </Label>
                <MultiSelect
                  value={selectedUniversities}
                  onValueChange={handleUniversityChange}
                >
                  <MultiSelectTrigger className="w-full h-10 bg-[#fcfcfc] border border-[var(--color-black-2)] rounded-[12px] px-3 py-2 text-[14px] leading-[1.35] tracking-[-0.56px] text-[#505e59] font-normal">
                    <MultiSelectValue
                      placeholder={
                        universitiesLoading
                          ? "Loading..."
                          : "Select universities"
                      }
                      options={universityOptions}
                    />
                  </MultiSelectTrigger>
                  <MultiSelectContent>
                    {universityOptions.map(
                      (option: { value: string; label: string }) => (
                        <MultiSelectItem
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </MultiSelectItem>
                      )
                    )}
                  </MultiSelectContent>
                </MultiSelect>
              </div>

              <div className="space-y-2">
                <Label className="text-[14px] leading-[1.35] tracking-[-0.56px] text-[#051912] font-normal mb-0 pb-1.5">
                  <HighlightText text="Course" />
                </Label>
                <MultiSelect
                  value={selectedCourses}
                  onValueChange={handleCourseChange}
                >
                  <MultiSelectTrigger className="w-full h-10 bg-[#fcfcfc] border border-[var(--color-black-2)] rounded-[12px] px-3 py-2 text-[14px] leading-[1.35] tracking-[-0.56px] text-[#505e59] font-normal">
                    <MultiSelectValue
                      placeholder={
                        coursesLoading ? "Loading..." : "Select courses"
                      }
                      options={courseOptions}
                    />
                  </MultiSelectTrigger>
                  <MultiSelectContent>
                    {courseOptions.map(
                      (option: { value: string; label: string }) => (
                        <MultiSelectItem
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </MultiSelectItem>
                      )
                    )}
                  </MultiSelectContent>
                </MultiSelect>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
